<template>
  <div class="card">
    
    <div class="card-body">
      <div class="d-flex align-items-center mb-0">
        <div class="d-flex align-items-center">
          <input
            type="date"
            class="form-control form-control-sm me-2"
            v-model="fecha"
            @change="onFechaChange"
          />
          <!-- selector de sede para filtrar -->
          <select class="form-select form-select-sm me-2" v-model="selectedSede">
            <!-- <option value="">Todas las sedes</option> -->
            <option v-for="s in sedes" :key="s" :value="s">{{ s }}</option>
          </select>

          <button class="btn btn-sm btn-primary" :disabled="loading" @click="fetchDatos" title="Actualizar datos">
            <span v-if="loading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            <b-icon v-else icon="arrow-clockwise" aria-hidden="true"></b-icon>
          </button>
        </div>
      </div>
      <!-- Etiqueta personalizada kWh/día -->
      <!-- <div class="custom-x-axis">
        <div class="axis-label" style="left: 10%; transform: translateX(-50%);">
          <div class="label-hora">kWh/día</div>
        </div>
      </div> -->

      <div v-if="error" class="alert alert-danger py-2">{{ error }}</div>

      <!-- Spinner solo cuando loading=true -->
      <div v-if="loading" class="d-flex justify-content-center align-items-center" style="min-height:260px;">
        <div class="text-center">
          <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
          <div class="mt-2 text-muted">Cargando...</div>
        </div>
      </div>

      <!-- Si no hay datos y no está cargando, mostrar solo el mensaje -->
      <div v-else-if="!hasChartData" class="d-flex justify-content-center align-items-center" style="min-height:260px;">
        <div class="text-center text-muted">Sin datos para la fecha/sede seleccionada</div>
      </div>

      <!-- ChartBar se renderiza sólo cuando chartData está construido y tiene labels -->
      <div v-else class="chart-container">
        <div class="chart-wrapper">
          <ChartBar :data="chartData" :height="400" title="Consumo total de energia por bloques" titleEjeY="kWh/día" />
          
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import ChartBar from '../Charts/ChartBar.vue';

export default {
  name: 'Grafico',
  components: { ChartBar },
  props: { defaultSede: { type: String, default: '' } },
  data() {
    const hoy = new Date();
    const yyyy = hoy.getFullYear();
    const mm = String(hoy.getMonth() + 1).padStart(2, '0');
    const dd = String(hoy.getDate()).padStart(2, '0');
    return {
      fecha: `${yyyy}-${mm}-${dd}`,
      loading: false,
      error: null,
      chartData: null,
      labels: [],
      values: [],
      rows: [],
      sedes: [],
      selectedSede: '',
      // Fetch periódico: ejecutar fetchDatos cada X ms después del primer fetch
      periodicIntervalId: null,
      periodicIntervalMs: 60 * 60 * 1000 // 1 hora
    };
  },
  computed: {
    hasChartData() {
      return this.chartData && Array.isArray(this.chartData.labels) && this.chartData.labels.length > 0;
    }
  },
  mounted() {
    // Ejecutar fetch inicial y, al terminar, programar ejecuciones periódicas cada 5 minutos
    this.fetchDatos().finally(() => {
      if (!this.periodicIntervalId) {
        this.periodicIntervalId = setInterval(() => {
          // llamar fetchDatos periódicamente; ignorar errores internos
          this.fetchDatos().catch(() => {});
        }, this.periodicIntervalMs);
      }
    });
  },
  beforeDestroy() {
    if (this.periodicIntervalId) {
      clearInterval(this.periodicIntervalId);
      this.periodicIntervalId = null;
    }
  },
  watch: {
    selectedSede() {
      this.buildLabelsValues(this.rows, this.selectedSede);
      this.initializeChart();
    }
  },
  methods: {
    onFechaChange() {
      // ejecutar al cambiar la fecha
      this.fetchDatos();
    },
    async fetchDatos() {
      this.loading = true;
      this.error = null;
      try {
        const payload = { fecha: this.fecha }; // backend espera YYYY-MM-DD
        const resp = await axios.post('/api/lecturas-por-fecha-con-bloque', payload, {
          headers: { Authorization: `Bearer ${this.$store.state.user?.token || ''}` }
        });
        // Normalizar respuesta: buscar array de lecturas en resp.data
        const rows = resp.data?.lecturas ?? resp.data ?? [];
        // console.log('Respuesta lecturas por fecha:', rows);
        if (!Array.isArray(rows)) {
          throw new Error('Respuesta inesperada del servidor');
        }

        // guardar resultados y generar lista de sedes únicas
        this.rows = rows;
        this.sedes = this.extractSedes(rows);

        // seleccionar sede por defecto:
        // prioridad: selectedSede ya existente -> prop defaultSede (si existe en la lista) -> primera sede de la lista -> ''
        if (!this.selectedSede) {
          if (this.defaultSede && this.sedes.includes(this.defaultSede)) {
            this.selectedSede = this.defaultSede;
          } else if (this.sedes.length > 0) {
            this.selectedSede = this.sedes[0];
          } else {
            this.selectedSede = '';
          }
        } else {
          // si ya había una selectedSede pero no existe en la nueva lista, resetear a opción válida
          if (this.selectedSede && !this.sedes.includes(this.selectedSede)) {
            if (this.defaultSede && this.sedes.includes(this.defaultSede)) {
              this.selectedSede = this.defaultSede;
            } else {
              this.selectedSede = this.sedes[0] ?? '';
            }
          }
        }

        // construir labels/values filtrando por la sede seleccionada
        this.buildLabelsValues(this.rows, this.selectedSede);

        this.initializeChart();
      } catch (err) {
        console.error('Error cargando lecturas por fecha:', err);
        this.error = (err.response && err.response.data && (err.response.data.message || JSON.stringify(err.response.data))) || err.message || 'Error al cargar datos';
      } finally {
        this.loading = false;
      }
    },

    // recorre los resultados y retorna un array único con los nombres de las sedes
    extractSedes(rows) {
      const names = new Set();
      const getName = (r) => {
        return (
            r.sede ??  
            ''
        );
      };
      rows.forEach(r => {
        let n = String(getName(r)).trim();
        // Eliminar la palabra "SEDES" del inicio del nombre para mejor visualización
        n = n.replace(/^SEDES?\s*/i, '').trim();
        if (n) names.add(n);
      });
      return Array.from(names).sort((a,b) => a.localeCompare(b, 'es'));
    },

    // construye labels y values a partir de rows, opcionalmente filtrando por sede
    // suma la ENERGÍA consumida por bloque usando calcularEnergiaConsumida(lectura)
    buildLabelsValues(rows, sede = '') {
        let filtered = rows;
        if (sede) {
            filtered = rows.filter(r => {
                let n = (r.sede ?? '').toString().trim();
                // Aplicar la misma lógica de eliminación de "SEDES" para el filtrado
                n = n.replace(/^SEDES?\s*/i, '').trim();
                return n === sede;
            });
        }

        // Agrupar por bloque/hora/intervalo sumando la energía calculada por lectura
        const map = new Map();
        filtered.forEach(r => {
            const key = (r.bloque_edificio ?? '').toString();
            if (!key) return;
            // calcular energía consumida a partir de la lectura
            let energia = 0;
            try {
                energia = Number(this.calcularEnergiaConsumida(r)) || 0;
            } catch (e) {
                energia = Number(r.valor ?? r.valor_kwh ?? r.kwh ?? r.energy ?? r.value ?? 0) || 0;
            }
            energia = energia / 1000; // convertir a kWh si está en Wh
            const prev = map.get(key) || 0;
            map.set(key, prev + energia);
        });

        // console.log('Datos agrupados por bloque:', map);

        // ordenar por clave si son horas en formato HH:MM o números
        const entries = Array.from(map.entries()).sort((a,b) => {
            const ka = a[0], kb = b[0];
            const na = Number(ka.replace(/[^0-9:]/g,'')) || NaN;
            const nb = Number(kb.replace(/[^0-9:]/g,'')) || NaN;
            if (!isNaN(na) && !isNaN(nb)) return na - nb;
            return ka.localeCompare(kb, 'es');
        });

        this.labels = entries.map(e => e[0].toLowerCase()); // Convertir a minúsculas
        // redondear a 2 decimales
        this.values = entries.map(e => Number((e[1] || 0).toFixed(2)));

       
    },

    initializeChart() {
      this.label = 'kWh';
      this.chartData = {
        labels: this.labels,
        datasets: [
          {
            label: this.label,
            data: this.values,
            backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'],
            borderColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'],
          }
        ]
      };
    },

    calcularEnergiaConsumida(lectura) {
      // usa campos de lectura para calcular la energía consumida en kWh
      // si los campos esperados no existen, devuelve 0
      let potencia = 0;
      if (!lectura) return 0;
      if (typeof lectura.potencia_interior !== 'undefined' || typeof lectura.potencia_exterior !== 'undefined') {
        if (lectura.tiporede_id === 1 || lectura.tiporede_id === 2) {
          potencia = (Number(lectura.potencia_exterior) || 0) + (Number(lectura.potencia_interior) || 0);
        } else {
          potencia = (3 * (Number(lectura.potencia_exterior) || 0) * (Number(lectura.fp_exterior) || 0)) + (Number(lectura.potencia_interior) || 0);
        }
        const tiempoOn = Number(lectura.tiempo_on) || 0; // segundos
        const energia = (String(lectura.estado).toUpperCase() === 'ON') ? (potencia * (tiempoOn / 3600)) : 0;
        return energia;
      }
      // fallback: si ya viene campo valor/kwh en la lectura
      return Number(lectura.valor ?? lectura.valor_kwh ?? lectura.kwh ?? lectura.energy ?? lectura.value ?? 0) || 0;
    }
  }
};
</script>

<style scoped>
.card { 
  border-radius: 0.5rem; 
  overflow: hidden; 
  margin-bottom: 0;
  padding-bottom: 0 !important;
}

.card-body { 
  /* padding: 0.5rem 0.5rem 55px 0.5rem !important;
  margin-bottom: 0 !important; */
}

.chart-container { 
  width: 100%; 
  height: 100%;
  display: flex; 
  flex-direction: column;
  justify-content: flex-start; 
  align-items: stretch;
  margin-bottom: 0;
  padding-bottom: 0;
  overflow: visible;
}

/* Eliminar cualquier espacio del componente Chart interno */
:deep(.chart-container) {
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
}

.chart-wrapper {
  width: 100%;
  max-width: 100%;
  flex: 1;
  min-height: 0;
  position: relative;
  margin-bottom: 0;
  padding-bottom: 0;
  display: flex;
  flex-direction: column;
}

.custom-x-axis {
  position: relative;
  width: 100%;
  height: 40px;
  margin-top: 5px;
  z-index: 100;
  display: flex;
  justify-content: center;
  align-items: center;
}

.axis-label {
  position: absolute;
  color: #495057;
  background: rgba(255, 255, 255, 0.95);
  padding: 4px 8px;
  border-radius: 6px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  white-space: nowrap;
  border: 1px solid rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.label-hora {
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1;
  color: #343a40;
}
</style>